// The phone shell. Default state is a 48-px top bar and the document —
// nothing else. Everything the desktop spreads across a forty-control
// toolbar is summoned: the tool palette and every command from the ☰
// drawer, an object's controls from the bottom bar when there IS an
// object, and the context menu from a long press on the page.
//
// Nothing here re-implements the editor. The drawer renders the same
// EditorCommand registry that backs the keyboard and the command
// palette, and the same TOOL_GROUPS the desktop toolbar renders; the
// bottom bar is literally the desktop's PropsBar. One source, two
// shells — a command added to the registry appears in both.
import { useEffect, useState } from "react";
import {
  commandGroup,
  shortcutLabel,
  TOOL_GROUPS,
  toolDef,
  toolMeta,
  type CommandGroup,
  type EditorCommand,
} from "../lib/commands.ts";
import type { Tool } from "../lib/ui-types.ts";
import { PropsBar, propsBarHasContent, type PropsBarProps } from "./PropsBar.tsx";

/** Drawer sections, in the order people ask for things — not the order
 *  the desktop toolbar happens to sit in. */
const SECTIONS: readonly { group: CommandGroup; label: string }[] = [
  { group: "file", label: "File" },
  { group: "edit", label: "Edit" },
  { group: "view", label: "View" },
  { group: "panel", label: "Panels" },
  { group: "document", label: "Document" },
  { group: "help", label: "Help" },
];

/** The shell is two pieces because the document goes between them: the
 *  bar and its drawer above, the selection's controls below. The drawer's
 *  open state lives in the route for the same reason. */
export function MobileTopBar({
  docName,
  tool,
  toolLocked,
  onPickTool,
  onToggleLock,
  commands,
  drawer,
  setDrawer,
  tabs,
}: {
  docName: string;
  tool: Tool;
  toolLocked: boolean;
  onPickTool: (t: Tool) => void;
  onToggleLock: () => void;
  commands: readonly EditorCommand[];
  drawer: boolean;
  setDrawer: (open: boolean) => void;
  /** Rendered inside the drawer's header — the document tab switcher. */
  tabs?: React.ReactNode;
}) {
  const byId = (id: string) => commands.find((c) => c.id === id);
  const undo = byId("edit.undo");
  const redo = byId("edit.redo");

  // A drawer that survives a back gesture or an Escape is a drawer you
  // can't get out of one-handed.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawer(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);

  return (
    <>
      {/* top bar — the only chrome that is always on screen */}
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-[color:var(--color-border)] px-1">
        <button
          type="button"
          aria-label="Menu"
          aria-expanded={drawer}
          onClick={() => setDrawer(true)}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-menu-line text-xl" aria-hidden />
        </button>
        <span className="min-w-0 flex-1 truncate px-1 text-sm font-medium">{docName}</span>
        {[undo, redo].map(
          (c) =>
            c && (
              <button
                key={c.id}
                type="button"
                aria-label={c.label}
                disabled={c.disabled}
                onClick={c.run}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
              >
                <i className={c.icon} aria-hidden />
              </button>
            )
        )}
      </div>

      {drawer && (
        <>
          <div
            className="absolute inset-0 z-40 bg-black/40"
            onClick={() => setDrawer(false)}
            aria-hidden
          />
          <div
            role="dialog"
            aria-label="Editor menu"
            className="absolute inset-y-0 left-0 z-50 flex w-[min(20rem,85vw)] flex-col bg-[color:var(--color-bg)] shadow-2xl"
          >
            <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--color-border)] px-2">
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setDrawer(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
              <span className="truncate text-sm font-semibold">{docName}</span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pb-6">
              {tabs && <div className="border-b border-[color:var(--color-border)]">{tabs}</div>}

              {/* Tools first: it's what people open the menu for. Big
                  targets in a grid rather than a list of thin rows. */}
              <Section label="Tools">
                <div className="grid grid-cols-3 gap-1 px-2">
                  {[toolDef("select"), toolDef("hand")].map((d) => (
                    <ToolTile
                      key={d.tool}
                      icon={d.icon}
                      label={d.label}
                      active={tool === d.tool}
                      onClick={() => {
                        onPickTool(d.tool);
                        setDrawer(false);
                      }}
                    />
                  ))}
                  {TOOL_GROUPS.flatMap((g) => g.members).map((m) => (
                    <ToolTile
                      key={m.tool}
                      icon={m.icon}
                      label={m.label}
                      active={tool === m.tool}
                      onClick={() => {
                        onPickTool(m.tool);
                        setDrawer(false);
                      }}
                      onHold={() => {
                        onPickTool(m.tool);
                        onToggleLock();
                        setDrawer(false);
                      }}
                    />
                  ))}
                </div>
              </Section>

              {SECTIONS.map(({ group, label }) => {
                const items = commandGroup(commands, group);
                if (items.length === 0) return null;
                return (
                  <Section key={group} label={label}>
                    {items.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        disabled={c.disabled}
                        onClick={() => {
                          c.run();
                          setDrawer(false);
                        }}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm disabled:opacity-40 ${
                          c.active
                            ? "text-[color:var(--color-accent)]"
                            : "hover:bg-[color:var(--color-bg-subtle)]"
                        }`}
                      >
                        <i className={c.icon ?? "ri-checkbox-blank-circle-line"} aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{c.label}</span>
                        {shortcutLabel(c.shortcut) && (
                          <span className="text-xs text-[color:var(--color-fg-muted)]">
                            {shortcutLabel(c.shortcut)}
                          </span>
                        )}
                      </button>
                    ))}
                  </Section>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function MobileBottomBar({
  tool,
  toolLocked,
  onPickTool,
  propsBar,
}: {
  tool: Tool;
  toolLocked: boolean;
  onPickTool: (t: Tool) => void;
  propsBar: PropsBarProps;
}) {
  const showProps = propsBarHasContent(propsBar);
  const armed = tool !== "select" && tool !== "hand";
  if (!armed && !showProps) return null;
  return (
    <>
      {/* armed-tool chip — with the toolbar hidden, this is the only
          thing that says which mode a press is in. Tapping it disarms. */}
      {armed && (
        <button
          type="button"
          onClick={() => onPickTool("select")}
          style={{ bottom: showProps ? "calc(3.5rem + env(safe-area-inset-bottom, 0px) + 0.5rem)" : "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
          className="absolute left-3 z-30 inline-flex h-9 items-center gap-1.5 rounded-full bg-[color:var(--color-accent)] px-3 text-xs font-medium text-[color:var(--color-accent-fg)] shadow-lg"
        >
          <i className={toolMeta(tool).icon} aria-hidden />
          {toolMeta(tool).label}
          {toolLocked && <i className="ri-pushpin-fill text-[10px]" aria-hidden />}
          <i className="ri-close-line opacity-80" aria-hidden />
        </button>
      )}

      {/* bottom bar — only when there IS something to act on */}
      {showProps && (
        <div
          role="toolbar"
          aria-label="Properties"
          className="flex h-14 shrink-0 items-center gap-1 overflow-x-auto border-t border-[color:var(--color-border)] px-2"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <PropsBar {...propsBar} />
        </div>
      )}

    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="py-2">
      <h2 className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-fg-muted)]">
        {label}
      </h2>
      {children}
    </section>
  );
}

/** A 56-px tool target. Holding one locks the tool, the same gesture the
 *  desktop's ToolMenu uses. */
function ToolTile({
  icon,
  label,
  active,
  onClick,
  onHold,
}: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
  onHold?: () => void;
}) {
  const [held, setHeld] = useState(false);
  let timer: number | undefined;
  return (
    <button
      type="button"
      aria-pressed={active}
      onPointerDown={() => {
        if (!onHold) return;
        timer = window.setTimeout(() => {
          setHeld(true);
          onHold();
        }, 450);
      }}
      onPointerUp={() => window.clearTimeout(timer)}
      onPointerLeave={() => window.clearTimeout(timer)}
      onClick={() => {
        if (held) {
          setHeld(false);
          return;
        }
        onClick();
      }}
      className={`flex h-14 flex-col items-center justify-center gap-0.5 rounded-md text-[11px] ${
        active
          ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
          : "bg-[color:var(--color-bg-subtle)] hover:bg-[color:var(--color-border)]"
      }`}
    >
      <i className={`${icon} text-lg`} aria-hidden />
      {label}
    </button>
  );
}
